import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { useStore } from '../state/patientProductionStore';
import { patientApi } from '../api';
import { GOV_COMPLIANCE, govPageContainer } from '../ui/PatientUIGovernor';

const PAGE_SIZE = 25;

/**
 * Signing in is the commonest thing anyone does, and it is not care. The
 * default view leaves it out — a real account's first page was 48 rows of
 * "Logged in" before it reached an ambulance — and the Security chip brings
 * it back, because it is still the patient's to see.
 */
const SECURITY = 'security';

const TYPE_COLORS: Record<string, string> = {
  appointment: T.teal, lab: T.info, hospital: T.navy, pharmacy: '#059669',
  emergency: T.danger, ambulance: T.danger, consent: T.warning,
  insurance: T.teal, medicine: T.info, payment: '#059669',
  account: T.navy, security: T.textSec, record: T.navy, general: T.textSec,
};

const TYPE_ICONS: Record<string, string> = {
  appointment: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  lab: 'M14.5 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V7.5L14.5 2z',
  hospital: 'M3 21h18 M5 21V7l7-4 7 4v14 M9 12h6 M12 9v6',
  pharmacy: 'M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z',
  emergency: 'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z M12 9v4 M12 17h.01',
  ambulance: 'M3 17V7a1 1 0 011-1h10v11H3z M14 10h4l3 3v4h-7v-7z M7 20a2 2 0 100-4 2 2 0 000 4z M17 20a2 2 0 100-4 2 2 0 000 4z',
  consent: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  insurance: 'M9 12l2 2 4-4 M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  medicine: 'M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5',
  payment: 'M2 7h20v12a1 1 0 01-1 1H3a1 1 0 01-1-1V7z M2 11h20 M6 16h4',
  account: 'M12 11a4 4 0 100-8 4 4 0 000 8z M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2',
  security: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z M9 12l2 2 4-4',
  record: 'M9 17H5a2 2 0 01-2-2V5a2 2 0 012-2h4 M15 3h4a2 2 0 012 2v10a2 2 0 01-2 2h-4',
  general: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
};

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function sanitizeText(text: string | undefined): string {
  if (!text) return '';
  const cleaned = text.replace(/^[,\s]+|[,\s]+$/g, '').replace(/,\s*,+/g, ',').replace(/\s{2,}/g, ' ').trim();
  if (!cleaned || /^[,\s.·-]+$/.test(cleaned)) return '';
  return cleaned;
}

/**
 * One row as this page draws it. Deliberately NOT the store's `TimelineEvent`:
 * that pins `type` to a fixed union the store knows about, while the server's
 * vocabulary is its own and grows — forcing the two together is what made the
 * old code cast the whole list to `any`.
 */
interface TimelineRow {
  id: string;
  type: string;
  title: string;
  description?: string;
  date: string;
  status?: string;
  linkedRoute?: string;
}

/** "APPOINTMENT" → "appointment"; the chip label capitalises it back. */
const slug = (type: string) => (type || 'general').toLowerCase().replace(/_/g, '');

export default function HealthTimeline() {
  const navigate = useNavigate();
  const localTimeline = useStore(s => s.timeline);
  const [apiEvents, setApiEvents] = useState<TimelineRow[]>([]);
  // The filter vocabulary the SERVER can return. The page used to carry a
  // hand-written list of thirteen: seven matched no type at all and could only
  // ever say "no events", and Ambulance — which the server really returns —
  // had no chip.
  const [serverTypes, setServerTypes] = useState<string[]>([]);
  const [filter, setFilter] = useState<string>('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async (which: string, nextPage: number, append: boolean) => {
    setLoading(true);
    try {
      const res = await patientApi.getTimeline({
        page: nextPage,
        limit: PAGE_SIZE,
        ...(which === 'all' ? { exclude: SECURITY.toUpperCase() } : { type: which.toUpperCase() }),
      });
      const rows: TimelineRow[] = res.events.map(e => ({
        id: e.id,
        type: slug(e.eventType),
        title: sanitizeText(e.title),
        description: sanitizeText(e.description),
        date: e.eventDate,
        status: e.status,
        linkedRoute: undefined,
      })).filter(e => e.title);
      setApiEvents(prev => (append ? [...prev, ...rows] : rows));
      setTotal(res.total);
      if (res.types.length) setServerTypes(res.types.map(slug));
      setLoadError(false);
    } catch {
      setLoadError(true);
      if (!append) setApiEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { setPage(1); load(filter, 1, false); }, [filter, load]);

  const cleanLocal: TimelineRow[] = localTimeline.map(e => ({ ...e, title: sanitizeText(e.title) || e.title, description: sanitizeText(e.description) }));
  // A store event and the backend audit event for the same action carry
  // unrelated ids, so dedupe by proximity: drop the API copy when a local
  // event of the same type sits within a few minutes of it (they are written
  // in the same breath as the booking call).
  const apiDeduped = apiEvents.filter(a =>
    !cleanLocal.some(l => l.type === a.type && Math.abs(new Date(l.date).getTime() - new Date(a.date).getTime()) < 3 * 60000));
  // Local mirrors only belong in the view they match; the Security chip is the
  // server's list alone.
  const localForView = filter === SECURITY
    ? []
    : cleanLocal.filter(l => filter === 'all' || l.type === filter);
  const events = [...localForView, ...apiDeduped].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const chips = ['all', ...serverTypes];
  // `total` counts the whole filtered set on the server, not the page in hand.
  const more = apiEvents.length < total;

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 9, color: T.textSec, fontFamily: T.font }}>
            {filter === 'all' ? 'Your care history — sign-ins are under Security' : 'Complete history of your healthcare journey'}
          </div>
        </div>
        <span style={{ fontSize: 9, fontWeight: 600, color: T.teal, background: `${T.teal}10`, padding: '3px 8px', borderRadius: 8, fontFamily: T.font }}>
          {total} {total === 1 ? 'event' : 'events'}
        </span>
      </div>

      {loadError && (
        <div style={{ fontSize: 11, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '8px 12px', fontFamily: T.font }}>
          Could not load your server timeline. Showing locally saved events only.
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
        {chips.map(t => (
          <button key={t} onClick={() => setFilter(t)} style={{
            flexShrink: 0, padding: '5px 10px', borderRadius: 8, border: `1px solid ${filter === t ? T.teal : T.border}`,
            background: filter === t ? T.teal : T.white, color: filter === t ? '#FFF' : T.textSec,
            fontSize: 9, fontWeight: 600, fontFamily: T.font, cursor: 'pointer', textTransform: 'capitalize',
          }}>{t === 'all' ? 'All care' : t}</button>
        ))}
      </div>

      {events.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={T.border} strokeWidth="1.5" style={{ margin: '0 auto 12px' }}><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" /></svg>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: T.font }}>
            {loading ? 'Loading your timeline…' : 'No timeline events yet'}
          </div>
          {!loading && (
            <div style={{ fontSize: 9, color: T.textSec, fontFamily: T.font, marginTop: 4 }}>
              {filter === 'all'
                ? 'Your healthcare journey events will appear here as you book appointments, order tests, and use NeoHealthCard services.'
                : `Nothing under ${filter} yet.`}
            </div>
          )}
        </div>
      ) : (
        <div style={{ position: 'relative', paddingLeft: 20 }}>
          <div style={{ position: 'absolute', left: 8, top: 0, bottom: 0, width: 2, background: T.border, borderRadius: 1 }} />
          {events.map(event => {
            const color = TYPE_COLORS[event.type] || T.textSec;
            const iconPath = TYPE_ICONS[event.type] || TYPE_ICONS.general;
            return (
              <div key={event.id} onClick={() => event.linkedRoute && navigate(event.linkedRoute)} style={{
                position: 'relative', marginBottom: 10, cursor: event.linkedRoute ? 'pointer' : 'default',
              }}>
                <div style={{
                  position: 'absolute', left: -16, top: 6, width: 18, height: 18, borderRadius: 9,
                  background: `${color}15`, border: `2px solid ${T.bg}`, display: 'grid', placeItems: 'center', zIndex: 1,
                }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round"><path d={iconPath} /></svg>
                </div>
                <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 12, boxShadow: T.shadow, marginLeft: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: T.text, fontFamily: T.font }}>{event.title}</div>
                      {event.description && <div style={{ fontSize: 9, color: T.textSec, fontFamily: T.font, marginTop: 2 }}>{event.description}</div>}
                    </div>
                    <span style={{ fontSize: 8, color: T.textTer, fontFamily: T.font, flexShrink: 0 }}>{relativeTime(event.date)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
                    <span style={{ display: 'inline-block', fontSize: 8, fontWeight: 600, color, background: `${color}10`, padding: '2px 6px', borderRadius: 4, fontFamily: T.font, textTransform: 'capitalize' }}>{event.type}</span>
                    {/* What happened, in the server's own word. This used to be
                        the constant 'SUCCESS' printed under every row. */}
                    {event.status && (
                      <span style={{ fontSize: 8, color: T.textTer, fontFamily: T.font }}>{event.status}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {more && (
        <button
          onClick={() => { const next = page + 1; setPage(next); load(filter, next, true); }}
          disabled={loading}
          style={{
            fontFamily: T.font, fontSize: 11, fontWeight: 700, color: T.teal, background: T.white,
            border: `1.5px solid ${T.teal}`, borderRadius: 10, padding: '9px 0', cursor: loading ? 'wait' : 'pointer',
          }}
        >
          {loading ? 'Loading…' : `Show older (${total - apiEvents.length} more)`}
        </button>
      )}

      <div style={{ ...GOV_COMPLIANCE.text, marginTop: 12 }}>
        NeoHealthCard™ · Your own activity record · Access by anyone else is listed under Privacy → Access log
      </div>
    </div>
  );
}
